import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import LandingPage from "./LandingPage";
import ForQCTeams from "./ForQCTeams";
import Consulting from "./Consulting";
import Privacy from "./Privacy";
import Terms from "./Terms";
import NotFound from "./NotFound";
import ExternalRedirect from "./ExternalRedirect";
import BlinkitOnboarding from "./BlinkitOnboarding";
import ZeptoOnboarding from "./ZeptoOnboarding";
import InstamartOnboarding from "./InstamartOnboarding";
import BigBasketOnboarding from "./BigBasketOnboarding";
import BlinkitCommissionStructure from "./BlinkitCommissionStructure";
import { PlatformStub, CategoryStub, GuideStub, PLATFORM_PAGES, CATEGORY_PAGES, GUIDE_PAGES } from "./SeoStubPage";

// Platform slugs that have shipped real, indexable content. These are
// excluded from the SeoStubPage loop below and wired to concrete routes.
const SHIPPED_PLATFORM_SLUGS = new Set([
  "blinkit-seller-onboarding",
  "zepto-seller-onboarding",
  "swiggy-instamart-seller-onboarding",
  "bigbasket-seller-onboarding",
]);
// Same idea for guide slugs (matched against seoPages.js slugs which start
// with "guides/").
const SHIPPED_GUIDE_SLUGS = new Set(["guides/blinkit-commission-structure"]);
import { Toaster } from "./components/ui/sonner";
import "./App.css";

const FEEDS_URL = "https://feeds.letspickquick.com/";

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-[rgb(17,17,19)] text-white">
          <Toaster richColors position="top-right" />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/for-quick-commerce" element={<ForQCTeams />} />
            <Route path="/for-platforms" element={<ForQCTeams />} />
            <Route path="/consulting" element={<Consulting />} />
            <Route path="/qc-consulting" element={<Consulting />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />

            {/* Shipped platform pages (real content, indexable). Must be
                declared BEFORE the stub loop so their concrete component
                wins the route match. */}
            <Route path="/blinkit-seller-onboarding" element={<BlinkitOnboarding />} />
            <Route path="/zepto-seller-onboarding" element={<ZeptoOnboarding />} />
            <Route path="/swiggy-instamart-seller-onboarding" element={<InstamartOnboarding />} />
            <Route path="/bigbasket-seller-onboarding" element={<BigBasketOnboarding />} />

            {/* Shipped guide pages. Same rule: declared before stub loop. */}
            <Route path="/guides/blinkit-commission-structure" element={<BlinkitCommissionStructure />} />

            {/* SEO stub routes: platform pages (root-level slugs), category
                pages (root-level slugs), and guide pages (under /guides/).
                Every stub is served with robots="noindex, nofollow" until
                its real copy ships. See src/data/seoPages.js. Shipped
                slugs are filtered out so they never render the stub. */}
            {PLATFORM_PAGES.filter((p) => !SHIPPED_PLATFORM_SLUGS.has(p.slug)).map((p) => (
              <Route key={p.slug} path={`/${p.slug}`} element={<PlatformStub />} />
            ))}
            {CATEGORY_PAGES.map((c) => (
              <Route key={c.slug} path={`/${c.slug}`} element={<CategoryStub />} />
            ))}
            {GUIDE_PAGES.filter((g) => !SHIPPED_GUIDE_SLUGS.has(g.slug)).map((g) => (
              <Route key={g.slug} path={`/${g.slug}`} element={<GuideStub />} />
            ))}

            {/* Retired /playbook: index route + all article slugs redirect to external feeds site. */}
            <Route path="/playbook" element={<ExternalRedirect to={FEEDS_URL} />} />
            <Route path="/playbook/*" element={<ExternalRedirect to={FEEDS_URL} />} />
            <Route path="/blog" element={<ExternalRedirect to={FEEDS_URL} />} />
            <Route path="/blog/*" element={<ExternalRedirect to={FEEDS_URL} />} />

            {/* Catch-all 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
